import React, { useState, useEffect } from "react";
import { FlatList, View, Text, Pressable, TouchableOpacity, Platform, StyleSheet } from "react-native";
import {
  ContentList,
  ContainerPlaylist,
  Content,
  Avatar,
  ViewCommentNotification,
} from "../style";
import useViewport from "../../../hooks/useViewport";
import ListHeader from "../../../components/ListHeader";
import THEME from "../../../config/theme";
import { ADMIN_FONT } from "../../../config/theme/adminColors";
import { StandardText } from "../../../config/theme/globalStyles";
import { ActivityIndicator } from "react-native-paper";
import { MaterialIcons, MaterialCommunityIcons, Ionicons } from "@expo/vector-icons";
import Toast from "react-native-toast-message";
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import { useCustomImages } from '../../../api/Brand';
import {
  buildContentCoverAiAssistantPayload,
  buildContentCoverReferenceOptions,
} from '../../../utils/buildCoverImageAiAssistantText';
import {
  buildCoverAiCopyToastMessage,
  copyCoverAiAssistantToClipboard,
} from '../../../utils/aiAssistantReferenceImages';
import { getCardCoverUrl } from '../../../utils/coverImageAiPromptShared';
import CopyContentModal from "../../../components/Modals/CopyContentModal";
import CoverAiReferencePickerModal from "../../../components/Modals/CoverAiReferencePickerModal";
import AdminActionMenu from "../../../components/AdminActionMenu";
import { downloadVideo } from "../../../services/panda";

const FlatListView = ({
  cardId,
  categoryId,
  videos,
  selectedId,
  onAddVideoClick,
  cardInformation,
  onEditVideoClick,
  onDeleteVideoClick,
  onEditVideoBackgroundClick,
  onVideoListClick,
  onEditCardInformationClick,
  onEditOrderClick,
  onDeleteCardClick,
  onReorderVideos,
  user,
}) => {
  const { width } = useViewport();
  const breakpoint = 1080;
  const smallScreen = width < breakpoint;
  const addAPhotoSize = smallScreen ? 30 : 40;

  const { customColor, customFont, customInfo } = useCustomTheme();
  const { data: logoAuth } = useCustomImages('logoAuth');
  const { data: imageApp } = useCustomImages('imageApp');
  const planType = customInfo?.planType;
  const videoHosting = customInfo?.videoHosting;
  const canShowDownloadButton =
    user?.isAdmin === true && customInfo?.downloadMaterials === true;

  //adminRole "supportOnly" only allows access to notiications, community and comments
  const hasAdminRole = !user?.adminRole

  // estado para controlar o modal de cópia
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [videoToCopyDocId, setVideoToCopyDocId] = useState(null);

  // estado para loading de download
  const [downloadingId, setDownloadingId] = useState(null);

  const [coverAiPicker, setCoverAiPicker] = useState({
    visible: false,
    item: null,
    options: [],
  });
  const [coverAiLoading, setCoverAiLoading] = useState(false);

  // ── Reordenação de conteúdos por drag and drop (admin, desktop web) ────────
  // HTML5 DnD num wrapper <div> — seguro porque o react-native-web renderiza em
  // DOM; no app NATIVO o wrapper não é aplicado (um <div> quebraria o render).
  // Restrito ao desktop: arrastar por toque (mobile) é impreciso — lá o admin
  // usa o modal "Ordem". `smallScreen` já reflete a largura da janela.
  const canDragVideos =
    hasAdminRole && !!onReorderVideos && Platform.OS === "web" && !smallScreen;
  const [localVideos, setLocalVideos] = useState(null);
  const [dragState, setDragState] = useState({ from: null, over: null });

  // Ordem otimista local vale até o pai reenviar os vídeos já persistidos.
  useEffect(() => {
    setLocalVideos(null);
  }, [videos]);

  const data = localVideos || videos;

  const handleVideoDrop = (from, to) => {
    setDragState({ from: null, over: null });
    if (from == null || to == null || from === to) return;
    const reordered = [...data];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setLocalVideos(reordered); // feedback imediato
    // updateVideoOrder grava order = índice para cada item, na ordem recebida.
    Promise.resolve(onReorderVideos(reordered)).catch((err) => {
      console.error("Falha ao salvar a nova ordem dos conteúdos:", err);
      setLocalVideos(null); // desfaz o otimismo: volta à ordem persistida
      Toast.show({
        type: "error",
        text1: "Não foi possível salvar a nova ordem dos conteúdos.",
        text2: "Tente novamente mais tarde",
      });
    });
  };

  // ── Botões "novo conteúdo / aula ao vivo / livestream" ────────────────────
  // Pílulas na cor do cliente (o fundo da tela é o dele, então cores fixas do
  // painel admin não teriam contraste garantido aqui). A ação principal é a
  // preenchida; as demais são contornadas.
  const buttonBg = customColor?.button?.backgroundApp || THEME.COLORS.PRIMARY_900;
  const buttonText = customColor?.button?.textApp || THEME.COLORS.TEXT_BUTTON;
  const pageText = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;

  // Duas linhas: "aula ao vivo" + "livestream" lado a lado, e "novo conteúdo"
  // (ação principal) ocupando a largura toda abaixo delas.
  // Sem padding no topo: a primeira linha alinha com o topo da imagem de capa
  // à esquerda (no desktop) e com o fim da capa (no mobile).
  const addBarStyle = {
    gap: 8,
    paddingBottom: 12,
  };

  // Função (e não componente) de propósito: declarado dentro do componente,
  // um <AddButton /> seria um tipo novo a cada render e remontaria os botões.
  const renderAddButton = ({ key, icon, label, primary, fullWidth, onPress }) => (
    <Pressable
      key={key}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        // Na linha de dois, `flex: 1` divide o espaço em partes iguais. O botão
        // de largura total é `stretch` — `flex` num container em coluna sem
        // altura definida zeraria a altura dele.
        fullWidth ? { alignSelf: 'stretch' } : { flex: 1, minWidth: 0 },
        {
          minHeight: 42,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          paddingHorizontal: 12,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: primary ? buttonBg : 'rgba(128,128,128,0.4)',
          backgroundColor: primary ? buttonBg : 'transparent',
          cursor: 'pointer',
        },
        (hovered || pressed) && { opacity: 0.85 },
      ]}
    >
      <Ionicons name={icon} size={17} color={primary ? buttonText : pageText} />
      <Text
        numberOfLines={1}
        style={{
          // flexShrink: no RN o padrão é 0; sem isto o rótulo transbordaria a
          // pílula em vez de truncar quando os dois botões dividem uma coluna
          // estreita.
          flexShrink: 1,
          // Controle EXCLUSIVO do admin: tipografia do painel, não a fonte da
          // marca do cliente. Só as CORES seguem o tema, porque a barra fica
          // sobre o fundo temático da tela. `system-ui` não carrega peso no
          // nome da família, então o destaque vem do fontWeight.
          fontFamily: ADMIN_FONT,
          fontWeight: '600',
          fontSize: 13,
          color: primary ? buttonText : pageText,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );

  // handler de download
  const handleDownloadPress = async (item) => {
    try {
      setDownloadingId(item.docId);
      await downloadVideo(item);
    } catch (err) {
      console.error(err);
    } finally {
      setDownloadingId(null);
    }
  };

  const runContentCoverAiCopy = async (item, selectedReference) => {
    setCoverAiLoading(true);
    try {
      const { text, attachmentUrls } = buildContentCoverAiAssistantPayload({
        contentTitle: item.title || '',
        contentType: item.type || 'content',
        moduleTitle: cardInformation?.title || '',
        moduleCoverUrl: getCardCoverUrl(cardInformation),
        excludeVideoId: item.docId,
        videos: data,
        visualContext: {
          colors: customColor,
          logoAuth,
          imageApp,
        },
        selectedReference,
      });
      const copyResult = await copyCoverAiAssistantToClipboard({ text, attachmentUrls });
      Toast.show({
        type: copyResult.copied ? 'success' : 'error',
        text1: buildCoverAiCopyToastMessage(copyResult),
      });
      setCoverAiPicker({ visible: false, item: null, options: [] });
    } catch (error) {
      console.error('Erro ao copiar prompt de capa de conteúdo:', error);
      Toast.show({
        type: 'error',
        text1: 'Não foi possível copiar. Tente novamente no navegador.',
      });
    } finally {
      setCoverAiLoading(false);
    }
  };

  const handleGenerateContentCoverWithAi = (item) => {
    const options = buildContentCoverReferenceOptions({
      videos: data,
      excludeVideoId: item.docId,
      moduleTitle: cardInformation?.title || '',
      moduleCoverUrl: getCardCoverUrl(cardInformation),
    });

    if (options.length === 0) {
      runContentCoverAiCopy(item, null);
      return;
    }

    setCoverAiPicker({ visible: true, item, options });
  };

  const handleConfirmContentCoverReference = (selectedReference) => {
    if (!coverAiPicker.item) return;
    runContentCoverAiCopy(coverAiPicker.item, selectedReference);
  };

  const renderVideoRow = (item, index) => {
    const canDownload =
      canShowDownloadButton &&
      (item?.pandaVideoMetadata?.video_external_id || item?.videoInfo?.metadata?.id);

    // Ações do conteúdo agrupadas num menu de 3 pontos (antes eram ícones soltos).
    const videoActions = [
      { label: "Editar", icon: "edit-3", onPress: () => onEditVideoClick(item) },
      {
        label: "Gerar capa com IA",
        icon: "auto-awesome",
        onPress: () => handleGenerateContentCoverWithAi(item),
      },
      {
        label: "Copiar",
        icon: "copy",
        onPress: () => {
          setVideoToCopyDocId(item.docId);
          setCopyModalVisible(true);
        },
      },
      ...(canDownload ? [{ label: "Baixar vídeo", icon: "download", onPress: () => handleDownloadPress(item) }] : []),
      { label: "Excluir", icon: "trash-2", danger: true, onPress: () => onDeleteVideoClick(item) },
    ];

    const row = (
      <TouchableOpacity
        style={{ marginVertical: 6 }}
        onPress={() => onVideoListClick(item)}
        activeOpacity={0.7}
      >
        <ContainerPlaylist
          style={{
            paddingVertical: 8,
            paddingRight: 4,
            borderBottomWidth: 1,
            borderBottomColor: 'rgba(0,0,0,0.08)',
            gap: 4,
          }}
        >
          <Avatar
            resizeMode="cover"
            source={{ uri: item.backgroundImage?.url || item.image }}
            imageStyle={{ borderRadius: 8 }}
            style={{ borderRadius: 8, overflow: 'hidden' }}
          >
            <View style={{ flexDirection: 'row', flex: 1 }}>
              <ViewCommentNotification />
              {hasAdminRole &&
                /* O rótulo aparece DENTRO da capa, e não numa bolha flutuante:
                   a miniatura usa `overflow: hidden` para arredondar os cantos,
                   então um tooltip posicionado fora dela era recortado e só se
                   via uma faixa escura.

                   Ícone branco sobre véu escuro: antes era
                   `THEME.COLORS.TEXT_MAIN` (#2e2e2e) direto na imagem, que some
                   em capas escuras. E o véu do antigo `ContentImage` nunca
                   existiu — o valor estava entre aspas
                   (`background-color: "rgba(...)"`), CSS inválido descartado
                   pelo styled-components. */
                <Pressable
                  onPress={() => onEditVideoBackgroundClick(item)}
                  accessibilityLabel="Alterar a capa deste conteúdo"
                  // data-no-drag: sem isto, um micro-arrasto ao clicar inicia o
                  // drag da linha e engole o clique.
                  {...(Platform.OS === "web" ? { dataSet: { noDrag: "true" } } : {})}
                  style={({ hovered }) => [
                    styles.coverButton,
                    hovered && styles.coverButtonHovered,
                  ]}
                >
                  {({ hovered }) => (
                    <>
                      {/* Encolhe no hover para abrir espaço ao rótulo dentro da
                          faixa, que é baixa (a miniatura tem 5rem de altura). */}
                      <MaterialIcons
                        name="add-a-photo"
                        size={hovered ? Math.round(addAPhotoSize * 0.6) : addAPhotoSize}
                        color="#FFFFFF"
                      />
                      {hovered ? (
                        <Text style={styles.coverLabel} numberOfLines={2}>
                          Alterar capa
                        </Text>
                      ) : null}
                    </>
                  )}
                </Pressable>
              }
              {item.hasNewComment && user?.isAdmin && (
                <ViewCommentNotification>
                  <MaterialCommunityIcons
                    name="comment-eye"
                    size={18}
                    color={customColor?.primary || THEME.COLORS.PRIMARY_900}
                  />
                </ViewCommentNotification>
              )}
            </View>
          </Avatar>
          <Content>
            <StandardText
              style={{
                fontFamily: customFont?.fontFamilySubtitle || THEME.FONTFAMILY.MEDIUM,
                fontSize: customFont?.fontMD || THEME.FONTSIZE.MEDIUM,
              }}
              color={customColor?.text?.app || THEME.COLORS.TEXT_MAIN}
              textAlign="flex-start"
              numberOfLines={2}
            >
              {item.title}
            </StandardText>
          </Content>
          {canDownload && downloadingId === item.docId && (
            <ActivityIndicator size={22} color={customColor.primary} style={{ marginRight: 4 }} />
          )}
          {hasAdminRole && <AdminActionMenu actions={videoActions} />}
        </ContainerPlaylist>
      </TouchableOpacity>
    );

    if (!canDragVideos) return row;

    const isDragging = dragState.from === index;
    const isDropTarget =
      dragState.from !== null && dragState.over === index && dragState.from !== index;
    const accent = customColor?.primary || THEME.COLORS.PRIMARY_800;
    // Barra indica onde o item solto vai cair (semântica do splice: vindo de
    // cima cai DEPOIS do alvo; de baixo, ANTES).
    const showTopBar = isDropTarget && dragState.from > index;
    const showBottomBar = isDropTarget && dragState.from < index;

    return (
      <div
        draggable
        onDragStart={(e) => {
          // Não iniciar o drag da linha quando o gesto começa sobre um controle
          // interativo (ex.: o menu de 3 pontos, marcado com data-no-drag). Sem
          // isto, o drag HTML5 engole o clique e o menu nunca abre.
          if (e.target?.closest?.("[data-no-drag]")) {
            e.preventDefault();
            return;
          }
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", String(index));
          setDragState({ from: index, over: null });
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setDragState((s) => (s.over === index ? s : { ...s, over: index }));
        }}
        onDrop={(e) => {
          e.preventDefault();
          const from = Number(e.dataTransfer.getData("text/plain"));
          handleVideoDrop(Number.isFinite(from) ? from : null, index);
        }}
        onDragEnd={() => setDragState({ from: null, over: null })}
        style={{
          cursor: "grab",
          opacity: isDragging ? 0.35 : 1,
          borderTop: `2px solid ${showTopBar ? accent : "transparent"}`,
          borderBottom: `2px solid ${showBottomBar ? accent : "transparent"}`,
          transition: "opacity 120ms ease",
        }}
      >
        {row}
      </div>
    );
  };

  const content = (
    <>
      {hasAdminRole &&
        <View style={addBarStyle}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {renderAddButton({
              key: 'live',
              icon: 'videocam-outline',
              label: 'Nova aula ao vivo',
              onPress: () => onAddVideoClick('live'),
            })}
            {planType === 'premium' && videoHosting === 'ownPanda' &&
              renderAddButton({
                key: 'livestream',
                icon: 'radio-outline',
                label: 'Nova livestream',
                onPress: () => onAddVideoClick('livestream'),
              })}
          </View>
          {renderAddButton({
            key: 'content',
            primary: true,
            fullWidth: true,
            icon: 'add',
            label: 'Novo conteúdo',
            onPress: () => onAddVideoClick('content'),
          })}
        </View>
      }

      {/* lista de vídeos */}
      <FlatList
        data={data}
        extraData={{ selectedId, dragState: canDragVideos ? dragState : null }}
        keyExtractor={item => item.docId}
        renderItem={({ item, index }) => renderVideoRow(item, index)}
        style={{ flexGrow: 0 }}
        ListHeaderComponent={
          <ListHeader
            cardId={cardId}
            categoryId={categoryId}
            video={cardInformation}
            onEditCardInformationClick={onEditCardInformationClick}
            onEditOrderClick={videos?.length > 1 ? onEditOrderClick : undefined}
            onDeleteCardClick={onDeleteCardClick}
            user={user}
            adminOptions
          />
        }
      />

      {videoToCopyDocId && (
        <CopyContentModal
          visible={copyModalVisible}
          onDismiss={() => setCopyModalVisible(false)}
          mode="video"
          sourceCategoryId={categoryId}
          sourceCardId={cardId}
          videoDocId={videoToCopyDocId}
          onSuccess={() => {
            // se precisar recarregar a lista, chame aqui
          }}
        />
      )}

      <CoverAiReferencePickerModal
        visible={coverAiPicker.visible}
        title="Escolher capa de referência"
        subtitle="Selecione qual capa usar como template neste módulo. O prompt será copiado e a imagem escolhida será baixada para anexar na IA da sua preferência (ChatGPT, Claude ou similar)."
        options={coverAiPicker.options}
        isLoading={coverAiLoading}
        onDismiss={() => {
          if (!coverAiLoading) {
            setCoverAiPicker({ visible: false, item: null, options: [] });
          }
        }}
        onConfirm={handleConfirmContentCoverReference}
      />
    </>
  );

  // Sem <Provider> aqui: o ClickCard já provê um no topo da tela, que hospeda o
  // Portal do Menu de 3 pontos. Antes cada LINHA tinha seu próprio <Provider>,
  // e abrir o Menu colapsava a lista (conteúdos "sumiam"). Envolver o retorno
  // num Provider também quebrava a largura (o wrapper flex:1 fazia o width:30%
  // do ContentList virar 30% de si mesmo) — por isso o ContentList é retornado
  // direto como filho da linha.
  return smallScreen ? (
    <View style={{ paddingHorizontal: "1rem" }}>{content}</View>
  ) : (
    // Altura reativa ao viewport atual (a do style.js é calculada só na
    // importação e não acompanha resize), alinhada à imagem à esquerda.
    <ContentList style={{ height: (width * 0.7 - 32) / 1.78, maxHeight: "82vh" }}>
      {content}
    </ContentList>
  );
};

const styles = StyleSheet.create({
  // Ocupa uma FAIXA da miniatura, não a capa inteira: 60% da largura com
  // respiro em cima e embaixo, as mesmas proporções do `ContentImage` que
  // existia antes. Com `flex: 1` o véu cobria quase todo o card.
  coverButton: {
    width: "60%",
    marginVertical: "0.8rem",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    paddingHorizontal: 4,
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.4)",
    cursor: "pointer",
    transitionDuration: "120ms",
    transitionProperty: "background-color",
  },
  coverButtonHovered: { backgroundColor: "rgba(0,0,0,0.7)" },
  // "Alterar capa": só o admin vê, e o véu escuro por trás é fixo (não vem do
  // tema), então aqui tanto a fonte quanto a cor são do painel.
  coverLabel: {
    fontFamily: ADMIN_FONT,
    fontWeight: "600",
    fontSize: 11,
    lineHeight: 13,
    color: "#FFFFFF",
    textAlign: "center",
  },
});

export default FlatListView;
